const DB_NAME = 'RidersPortalDB';
const DB_VERSION = 1;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create orders store
      if (!db.objectStoreNames.contains('orders')) {
        db.createObjectStore('orders', { keyPath: 'id' });
      }

      // Create auth store
      if (!db.objectStoreNames.contains('auth')) {
        db.createObjectStore('auth', { keyPath: 'key' });
      }

      // Create delivery attempts store
      if (!db.objectStoreNames.contains('deliveryAttempts')) {
        db.createObjectStore('deliveryAttempts', { 
          keyPath: 'id', 
          autoIncrement: true 
        });
      }
    };

    request.onsuccess = () => resolve(request.result);
  });
};

export const saveOrders = async (orders) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');

    // Clear existing orders
    store.clear();

    // Add new orders
    orders.forEach(order => {
      store.add(order);
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getStoredOrders = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readonly');
    const store = transaction.objectStore('orders');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

export const updateStoredOrder = async (orderId, updates) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');
    const getRequest = store.get(orderId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        resolve(null);
        return;
      }

      const updated = { ...existing, ...updates };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

export const removeStoredOrder = async (orderId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');
    const request = store.delete(orderId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const saveAuthData = async (authData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('auth', 'readwrite');
    const store = transaction.objectStore('auth');

    // Save auth data with expiration
    const data = {
      key: 'authData',
      ...authData,
      expiresAt: Date.now() + (12 * 60 * 60 * 1000) // 12 hours from now
    };

    store.put(data);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getAuthData = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('auth', 'readonly');
    const store = transaction.objectStore('auth');
    const request = store.get('authData');

    request.onsuccess = () => {
      const data = request.result;
      if (data && data.expiresAt > Date.now()) {
        resolve(data);
      } else {
        // Clear expired auth data
        const clearTx = db.transaction('auth', 'readwrite');
        clearTx.objectStore('auth').clear();
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Add function to save delivery attempts
export const saveDeliveryAttempt = async (attempt) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('deliveryAttempts', 'readwrite');
    const store = transaction.objectStore('deliveryAttempts');
    
    const request = store.add(attempt);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Add function to get stored delivery attempts
export const getStoredDeliveryAttempts = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('deliveryAttempts', 'readonly');
    const store = transaction.objectStore('deliveryAttempts');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Add function to clear delivery attempts
export const clearDeliveryAttempts = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('deliveryAttempts', 'readwrite');
    const store = transaction.objectStore('deliveryAttempts');
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const clearOrders = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const clearAllRiderOfflineData = async () => {
  await Promise.all([clearOrders(), clearDeliveryAttempts()]);
}; 